import Swal from 'sweetalert2';

const IMAGE_QUALITY_PRESETS = {
    original: 1,
    high: 0.8,
    medium: 0.6,
    low: 0.4,
};

const IMAGE_QUALITY_CACHE_TTL_MS = 60000;
let recentQualitySelection = {
    quality: null,
    timestamp: 0,
};

const IMAGE_QUALITY_OPTIONS = {
    original: 'Original (100%)',
    high: 'High (80%)',
    medium: 'Medium (60%)',
    low: 'Low (40%)',
};

const isFormData = (payload) => typeof FormData !== 'undefined' && payload instanceof FormData;

const isCompressibleImageFile = (value) => {
    if (typeof File === 'undefined') {
        return false;
    }

    if (!(value instanceof File)) {
        return false;
    }

    const mimeType = String(value.type || '').toLowerCase();
    if (!mimeType.startsWith('image/')) {
        return false;
    }

    // Skip formats that are not safe to re-encode with canvas.
    if (mimeType === 'image/gif' || mimeType === 'image/svg+xml' || mimeType === 'image/heic' || mimeType === 'image/heif') {
        return false;
    }

    return true;
};

const askImageUploadQuality = async (imageCount) => {
    const now = Date.now();
    if (
        recentQualitySelection.quality !== null &&
        now - recentQualitySelection.timestamp < IMAGE_QUALITY_CACHE_TTL_MS
    ) {
        return recentQualitySelection.quality;
    }

    try {
        const { value } = await Swal.fire({
            title: 'Select Image Upload Quality',
            text: `${imageCount} image file${imageCount > 1 ? 's' : ''} selected`,
            input: 'select',
            inputOptions: IMAGE_QUALITY_OPTIONS,
            inputValue: 'medium',
            confirmButtonText: 'Continue',
            allowOutsideClick: false,
            allowEscapeKey: false,
        });

        const selectedQuality = IMAGE_QUALITY_PRESETS[value] ?? IMAGE_QUALITY_PRESETS.medium;
        recentQualitySelection = {
            quality: selectedQuality,
            timestamp: Date.now(),
        };

        return selectedQuality;
    } catch (error) {
        return IMAGE_QUALITY_PRESETS.medium;
    }
};

const loadImageElement = async (file) => {
    const objectUrl = URL.createObjectURL(file);

    return new Promise((resolve, reject) => {
        const image = new Image();
        image.onload = () => {
            URL.revokeObjectURL(objectUrl);
            resolve(image);
        };
        image.onerror = (error) => {
            URL.revokeObjectURL(objectUrl);
            reject(error);
        };
        image.src = objectUrl;
    });
};

const compressImageFile = async (file, quality) => {
    if (!isCompressibleImageFile(file) || quality >= 1) {
        return file;
    }

    try {
        const image = await loadImageElement(file);
        const width = image.naturalWidth || image.width;
        const height = image.naturalHeight || image.height;

        const canvas = document.createElement('canvas');
        canvas.width = width;
        canvas.height = height;

        const context = canvas.getContext('2d');
        if (!context) {
            return file;
        }

        context.drawImage(image, 0, 0, width, height);

        const blob = await new Promise((resolve) => {
            canvas.toBlob((result) => resolve(result), file.type || 'image/jpeg', quality);
        });

        if (!blob) {
            return file;
        }

        const compressedFile = new File([blob], file.name, {
            type: blob.type || file.type,
            lastModified: Date.now(),
        });

        return compressedFile.size > 0 && compressedFile.size < file.size ? compressedFile : file;
    } catch (error) {
        return file;
    }
};

export const prepareFormDataForImageUpload = async (payload) => {
    if (!isFormData(payload)) {
        return payload;
    }

    const entries = Array.from(payload.entries());
    const imageFiles = entries.filter(([, value]) => isCompressibleImageFile(value));

    if (imageFiles.length === 0) {
        return payload;
    }

    const quality = await askImageUploadQuality(imageFiles.length);
    if (quality >= 1) {
        return payload;
    }

    const nextFormData = new FormData();

    for (const [key, value] of entries) {
        if (isCompressibleImageFile(value)) {
            const compressed = await compressImageFile(value, quality);
            nextFormData.append(key, compressed, compressed.name);
            continue;
        }

        if (value instanceof File) {
            nextFormData.append(key, value, value.name);
        } else {
            nextFormData.append(key, value);
        }
    }

    return nextFormData;
};
